import { z } from 'zod';
// ============ User Schemas ============
export const UserSchema = z.object({
    id: z.string(),
    email: z.string().email(),
    created_at: z.string(),
});
// ============ Theme Presets ============
export const THEME_PRESETS = {
    blue: {
        name: 'Blue (Default)',
        primaryColor: '#0066cc',
        headerBgColor: '#0066cc',
        headerTextColor: '#ffffff',
        chatBgColor: '#ffffff',
        userBubbleColor: '#0066cc',
        userTextColor: '#ffffff',
        assistantBubbleColor: '#f0f0f0',
        assistantTextColor: '#333333',
        headerSize: 'comfortable',
        logoLayout: 'badge',
        wideLogoAlign: 'center',
        logoSize: 'large',
        logoShape: 'rounded',
        logoBackground: 'subtle',
        launcherStyle: 'icon',
        panelSize: 'standard',
    },
    green: {
        name: 'Green',
        primaryColor: '#10b981',
        headerBgColor: '#10b981',
        headerTextColor: '#ffffff',
        chatBgColor: '#ffffff',
        userBubbleColor: '#10b981',
        userTextColor: '#ffffff',
        assistantBubbleColor: '#ecfdf5',
        assistantTextColor: '#065f46',
        headerSize: 'comfortable',
        logoLayout: 'badge',
        wideLogoAlign: 'center',
        logoSize: 'large',
        logoShape: 'rounded',
        logoBackground: 'subtle',
        launcherStyle: 'icon',
        panelSize: 'standard',
    },
    purple: {
        name: 'Purple',
        primaryColor: '#8b5cf6',
        headerBgColor: '#8b5cf6',
        headerTextColor: '#ffffff',
        chatBgColor: '#ffffff',
        userBubbleColor: '#8b5cf6',
        userTextColor: '#ffffff',
        assistantBubbleColor: '#f5f3ff',
        assistantTextColor: '#5b21b6',
        headerSize: 'comfortable',
        logoLayout: 'badge',
        wideLogoAlign: 'center',
        logoSize: 'large',
        logoShape: 'rounded',
        logoBackground: 'subtle',
        launcherStyle: 'icon',
        panelSize: 'standard',
    },
    red: {
        name: 'Red',
        primaryColor: '#ef4444',
        headerBgColor: '#ef4444',
        headerTextColor: '#ffffff',
        chatBgColor: '#ffffff',
        userBubbleColor: '#ef4444',
        userTextColor: '#ffffff',
        assistantBubbleColor: '#fef2f2',
        assistantTextColor: '#991b1b',
        headerSize: 'comfortable',
        logoLayout: 'badge',
        wideLogoAlign: 'center',
        logoSize: 'large',
        logoShape: 'rounded',
        logoBackground: 'subtle',
        launcherStyle: 'icon',
        panelSize: 'standard',
    },
    orange: {
        name: 'Orange',
        primaryColor: '#f97316',
        headerBgColor: '#f97316',
        headerTextColor: '#ffffff',
        chatBgColor: '#ffffff',
        userBubbleColor: '#f97316',
        userTextColor: '#ffffff',
        assistantBubbleColor: '#fff7ed',
        assistantTextColor: '#9a3412',
        headerSize: 'comfortable',
        logoLayout: 'badge',
        wideLogoAlign: 'center',
        logoSize: 'large',
        logoShape: 'rounded',
        logoBackground: 'subtle',
        launcherStyle: 'icon',
        panelSize: 'standard',
    },
    dark: {
        name: 'Dark',
        primaryColor: '#1f2937',
        headerBgColor: '#1f2937',
        headerTextColor: '#ffffff',
        chatBgColor: '#ffffff',
        userBubbleColor: '#374151',
        userTextColor: '#ffffff',
        assistantBubbleColor: '#f3f4f6',
        assistantTextColor: '#1f2937',
        headerSize: 'comfortable',
        logoLayout: 'badge',
        wideLogoAlign: 'center',
        logoSize: 'large',
        logoShape: 'rounded',
        logoBackground: 'subtle',
        launcherStyle: 'icon',
        panelSize: 'standard',
    },
};
export const DEFAULT_THEME = {
    primaryColor: '#0066cc',
    headerBgColor: '#0066cc',
    headerTextColor: '#ffffff',
    chatBgColor: '#ffffff',
    userBubbleColor: '#0066cc',
    userTextColor: '#ffffff',
    assistantBubbleColor: '#f0f0f0',
    assistantTextColor: '#333333',
    headerSize: 'legacy',
    logoLayout: 'badge',
    wideLogoAlign: 'center',
    logoSize: 'legacy',
    logoShape: 'square',
    logoBackground: 'none',
    launcherStyle: 'icon',
    panelSize: 'legacy',
};
// ============ Chatbot Schemas ============
export const ThemeSchema = z.object({
    primaryColor: z.string().default('#0066cc'),
    headerBgColor: z.string().default('#0066cc'),
    headerTextColor: z.string().default('#ffffff'),
    chatBgColor: z.string().default('#ffffff'),
    userBubbleColor: z.string().default('#0066cc'),
    userTextColor: z.string().default('#ffffff'),
    assistantBubbleColor: z.string().default('#f0f0f0'),
    assistantTextColor: z.string().default('#333333'),
    headerSize: z.enum(['legacy', 'compact', 'comfortable', 'showcase']).default('legacy'),
    logoLayout: z.enum(['badge', 'wide']).default('badge'),
    wideLogoAlign: z.enum(['center', 'left']).default('center'),
    logoSize: z.enum(['legacy', 'small', 'medium', 'large']).default('legacy'),
    logoShape: z.enum(['square', 'rounded', 'circle']).default('square'),
    logoBackground: z.enum(['none', 'subtle', 'solid']).default('none'),
    launcherStyle: z.enum(['icon', 'logo', 'logo-with-chat']).default('icon'),
    panelSize: z.enum(['legacy', 'compact', 'standard', 'large']).default('legacy'),
});
export const ChatbotSchema = z.object({
    id: z.string(),
    name: z.string().min(1).max(100),
    title: z.string().max(100).nullable(),
    instructions: z.string().max(10000).default('You are a helpful assistant.'),
    cta_label: z.string().max(100).nullable(),
    cta_url: z.string().url().nullable(),
    greeting: z.string().max(500).default('Hello! How can I help you today?'),
    theme_json: z.string().nullable(),
    logo_url: z.string().url().nullable(),
    vector_store_id: z.string().nullable(),
    editable_by_sales: z.boolean().default(false),
    is_testing: z.boolean().default(false),
    created_at: z.string(),
    updated_at: z.string(),
});
export const CreateChatbotSchema = z.object({
    name: z.string().min(1).max(100),
    title: z.string().max(100).optional(),
    instructions: z.string().max(10000).optional(),
    cta_label: z.string().max(100).optional(),
    cta_url: z.string().url().optional(),
    greeting: z.string().max(500).optional(),
    theme_json: z.string().optional(),
    logo_url: z.string().url().optional(),
});
export const UpdateChatbotSchema = CreateChatbotSchema.extend({
    editable_by_sales: z.boolean().optional(),
    is_testing: z.boolean().optional(),
    customer_chatbot_enabled: z.boolean().optional(),
    customer_email_responder_enabled: z.boolean().optional(),
}).partial();
export const CustomerVisibilitySchema = z.object({
    customer_chatbot_enabled: z.boolean(),
    customer_email_responder_enabled: z.boolean(),
}).refine((data) => data.customer_chatbot_enabled || data.customer_email_responder_enabled, { message: 'At least one tab must be enabled for customers' });
// ============ Domain Schemas ============
export const ChatbotDomainSchema = z.object({
    id: z.string(),
    chatbot_id: z.string(),
    domain: z.string().min(1).max(255),
});
export const AddDomainSchema = z.object({
    domain: z.string().min(1).max(255),
});
// ============ FAQ Schemas ============
export const FAQSchema = z.object({
    id: z.string(),
    chatbot_id: z.string(),
    question: z.string().min(1).max(500),
    answer: z.string().min(1).max(2000),
    sort_order: z.number().default(0),
    created_at: z.string(),
});
export const CreateFAQSchema = z.object({
    question: z.string().min(1).max(500),
    answer: z.string().min(1).max(2000),
    sort_order: z.number().optional(),
});
export const UpdateFAQSchema = CreateFAQSchema.partial();
// ============ File Schemas ============
export const FileStatusSchema = z.enum(['in_progress', 'completed', 'failed', 'cancelled']);
export const ChatbotFileSchema = z.object({
    id: z.string(),
    chatbot_id: z.string(),
    openai_file_id: z.string(),
    filename: z.string(),
    vector_store_file_id: z.string().nullable(),
    status: FileStatusSchema,
    created_at: z.string(),
});
// ============ User Role Schemas ============
export const UserRoleSchema = z.enum(['admin', 'sales', 'customer']);
// ============ Email Responder Schemas ============
export const EmailProviderSchema = z.enum(['gmail', 'outlook', 'forwarding']);
export const EmailConnectionStatusSchema = z.enum(['pending', 'active', 'disconnected', 'error']);
export const ResponseToneSchema = z.enum(['formal', 'professional', 'friendly', 'concise']);
export const EmailResponderConfigSchema = z.object({
    enabled: z.boolean().default(false),
    response_tone: ResponseToneSchema.default('professional'),
    email_signature: z.string().default(''),
    scan_interval_minutes: z.number().min(1).max(60).default(5),
    cc_forwarder_on_forward: z.boolean().default(false),
    exclude_newsletters: z.boolean().default(true),
});
export const TrainingEmailSchema = z.object({
    subject: z.string().default(''),
    incoming_body: z.string().min(1),
    response_body: z.string().min(1),
});
export const CreateCustomerSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
    assigned_chatbot_id: z.string().min(1),
});
export const SalesReferenceQuickAnswerSchema = z.object({
    id: z.string().trim().min(1, 'Quick answer id is required'),
    question: z.string(),
    shortAnswer: z.string(),
    ifTheyPush: z.string(),
    tags: z.array(z.string()),
});
export const SalesReferenceDetailCardSchema = z.object({
    title: z.string(),
    summary: z.string(),
    bullets: z.array(z.string()),
});
export const SalesReferenceDetailSectionSchema = z.object({
    id: z.string().trim().min(1, 'Detail section id is required'),
    title: z.string(),
    whenToUse: z.string(),
    summary: z.string(),
    cards: z.array(SalesReferenceDetailCardSchema),
});
export const InternalSalesReferenceContentSchema = z.object({
    defaultPitch: z.string(),
    useOrder: z.array(z.string()),
    strongestTalkingPoints: z.array(z.string()),
    escalationPreview: z.array(z.string()),
    quickAnswers: z.array(SalesReferenceQuickAnswerSchema),
    detailSections: z.array(SalesReferenceDetailSectionSchema),
    claimsToAvoid: z.array(z.string()),
    whenToEscalate: z.array(z.string()),
    safeClose: z.string(),
}).superRefine((content, ctx) => {
    const quickAnswerIds = new Set();
    const detailSectionIds = new Set();
    content.quickAnswers.forEach((answer, index) => {
        if (quickAnswerIds.has(answer.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate quick answer id: ${answer.id}`,
                path: ['quickAnswers', index, 'id'],
            });
            return;
        }
        quickAnswerIds.add(answer.id);
    });
    content.detailSections.forEach((section, index) => {
        if (detailSectionIds.has(section.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Duplicate detail section id: ${section.id}`,
                path: ['detailSections', index, 'id'],
            });
            return;
        }
        detailSectionIds.add(section.id);
    });
});
// ============ Password Validation ============
export const PASSWORD_CHECKS = [
    { key: 'length', label: '12+ characters', test: (pw) => pw.length >= 12 },
    { key: 'uppercase', label: 'One uppercase', test: (pw) => /[A-Z]/.test(pw) },
    { key: 'lowercase', label: 'One lowercase', test: (pw) => /[a-z]/.test(pw) },
    { key: 'number', label: 'One number', test: (pw) => /[0-9]/.test(pw) },
    { key: 'special', label: 'One special (!@#-)', test: (pw) => /[!@#$%^&*(),.?":{}|<>\-]/.test(pw) },
];
export function validatePasswordStrength(pw) {
    return {
        length: pw.length >= 12,
        uppercase: /[A-Z]/.test(pw),
        lowercase: /[a-z]/.test(pw),
        number: /[0-9]/.test(pw),
        special: /[!@#$%^&*(),.?":{}|<>\-]/.test(pw),
    };
}
export function isStrongPassword(pw) {
    const v = validatePasswordStrength(pw);
    return v.length && v.uppercase && v.lowercase && v.number && v.special;
}
export function isPasswordValid(v) {
    return v.length && v.uppercase && v.lowercase && v.number && v.special;
}
// ============ Auth Schemas ============
export const LoginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
});
// ============ Widget API Schemas ============
export const WidgetConfigSchema = z.object({
    chatbotId: z.string(),
    title: z.string(),
    greeting: z.string(),
    cta_label: z.string().nullable(),
    cta_url: z.string().nullable(),
    logo_url: z.string().nullable(),
    theme: ThemeSchema,
});
export const ChatRequestSchema = z.object({
    chatbotId: z.string(),
    sessionId: z.string(),
    userMessage: z.string().min(1).max(10000),
    previousResponseId: z.string().optional(),
});
//# sourceMappingURL=index.js.map